import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FINANCING_KIND_LABELS,
  FINANCING_OBJECT_LABELS,
  PROJECT_REPORT_DISCLAIMER,
  PROJECT_STATUS_LABELS,
  canMoveProject,
  formatCents,
  type ConvertProjectInput,
  type CreateProjectInput,
  type FinancingProjectDetail,
  type ProjectStatus,
} from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import { AffordabilityCard } from './AffordabilityCard';
import { FinancingTabs } from './FinancingTabs';
import { ProjectForm } from './ProjectForm';
import { STATUS_BADGE } from './ProjectsPage';
import { errorText, formatDay, percentText } from './shared';
import '../fleet/fleet.css';
import './financing.css';

const today = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(new Date());

/**
 * Vorhaben (KON-256): Eckdaten, Tragfähigkeit, Mappe als PDF, Status (geplant, eingereicht, bewilligt, abgelehnt) und der Vertrag, der aus einem bewilligten Vorhaben entsteht. Ein
 * eingereichtes oder bewilligtes Vorhaben lässt sich nicht ändern, weil die Bank die Zahlen kennt. Beim Einreichen hält Wirkia die Tragfähigkeitsrechnung fest.
 */
export function ProjectDetailPage() {
  const { id = '' } = useParams();
  const { can } = useSession();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  const [startDate, setStartDate] = useState(today());
  const [lender, setLender] = useState('');
  const [contractNumber, setContractNumber] = useState('');
  const [decision, setDecision] = useState<{ status: 'approved' | 'rejected'; on: string; note: string } | null>(null);
  const mayWrite = can('financing.write');

  const detail = useQuery({
    queryKey: ['financing-project', id],
    queryFn: async () => (await api<{ data: FinancingProjectDetail }>('GET', `/financing-projects/${id}`)).data,
    retry: (n, e) => !(e instanceof ApiError && e.status === 404) && n < 2,
  });
  const refresh = () => {
    for (const key of ['financing-project', 'financing-projects', 'financing-list', 'financing-overview']) client.invalidateQueries({ queryKey: [key] });
  };
  const update = useMutation({
    mutationFn: async (body: CreateProjectInput) => (await api<{ data: FinancingProjectDetail }>('PATCH', `/financing-projects/${id}`, body)).data,
    onSuccess: () => { setEditing(false); setError(null); refresh(); },
    onError: (e) => setError(errorText(e, 'Das Vorhaben konnte nicht gespeichert werden.')),
  });
  const move = useMutation({
    mutationFn: async (body: { status: ProjectStatus; on?: string; note?: string }) => (await api<{ data: FinancingProjectDetail }>('POST', `/financing-projects/${id}/status`, body)).data,
    onSuccess: () => { setDecision(null); setError(null); refresh(); },
    onError: (e) => setError(errorText(e, 'Der Status ließ sich nicht ändern.')),
  });
  const convert = useMutation({
    mutationFn: async (body: ConvertProjectInput) => await api<{ data: FinancingProjectDetail; financingId: string }>('POST', `/financing-projects/${id}/convert`, body),
    onSuccess: (r) => { refresh(); navigate(`/finanzierungen/${r.financingId}`); },
    onError: (e) => setError(errorText(e, 'Der Vertrag konnte nicht angelegt werden.')),
  });
  const remove = useMutation({
    mutationFn: async () => api('DELETE', `/financing-projects/${id}`),
    onSuccess: () => { refresh(); navigate('/finanzierungen/vorhaben'); },
    onError: (e) => setError(errorText(e, 'Das Vorhaben konnte nicht gelöscht werden.')),
  });

  if (detail.isLoading) return <p className="muted">Wird geladen …</p>;
  if (detail.isError || !detail.data) return <div className="alert alert--error">Das Vorhaben wurde nicht gefunden. <Link to="/finanzierungen/vorhaben">Zur Liste</Link></div>;
  const p = detail.data;
  const locked = p.status === 'submitted' || p.status === 'approved';
  const snap = p.submittedAssessment;

  return (
    <>
      <FinancingTabs />
      <Link to="/finanzierungen/vorhaben" className="back-link">← Alle Vorhaben</Link>
      <div className="page-head">
        <h1>{p.title}</h1>
        <span className={`fleet-badge ${STATUS_BADGE[p.status]}`}>{PROJECT_STATUS_LABELS[p.status]}</span>
        <span className="muted">{p.number}</span>
      </div>
      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {editing ? (
        <ProjectForm project={p} onSubmit={(b) => { setError(null); update.mutate(b); }} onCancel={() => setEditing(false)} pending={update.isPending} error={error} />
      ) : (
        <section className="card" aria-label="Eckdaten">
          <h2>Eckdaten</h2>
          <dl className="fleet-facts">
            <div><dt>Art</dt><dd>{FINANCING_KIND_LABELS[p.kind]}</dd></div>
            <div><dt>Gegenstand</dt><dd>{[FINANCING_OBJECT_LABELS[p.objectKind], p.vehiclePlate, p.objectDescription].filter(Boolean).join(', ')}</dd></div>
            {p.objectValueCents !== null && <div><dt>Wert netto</dt><dd>{formatCents(p.objectValueCents)}</dd></div>}
            {p.kind !== 'lease' && <div><dt>{p.kind === 'loan' ? 'Darlehensbetrag' : 'Finanzierter Kaufpreis'}</dt><dd>{p.principalCents !== null ? formatCents(p.principalCents) : '–'}</dd></div>}
            {p.annualRateBp !== null && p.kind !== 'lease' && <div><dt>Sollzins pro Jahr</dt><dd>{percentText(p.annualRateBp)}</dd></div>}
            <div><dt>Laufzeit</dt><dd>{p.termMonths} Monate</dd></div>
            <div><dt>Monatliche Rate (brutto)</dt><dd><strong>{p.monthlyCents !== null ? formatCents(p.monthlyCents) : '–'}</strong></dd></div>
            {p.downPaymentCents > 0 && <div><dt>{p.kind === 'lease' ? 'Sonderzahlung' : 'Anzahlung'}</dt><dd>{formatCents(p.downPaymentCents)}</dd></div>}
            {p.balloonCents > 0 && <div><dt>{p.kind === 'lease' ? 'Restwert' : 'Schlussrate'}</dt><dd>{formatCents(p.balloonCents)}</dd></div>}
            {p.feesCents > 0 && <div><dt>Gebühren</dt><dd>{formatCents(p.feesCents)}</dd></div>}
            <div><dt>Geplanter Beginn</dt><dd>{formatDay(p.plannedStart)}</dd></div>
            <div><dt>Beantragt bei</dt><dd>{p.lenderName ?? '–'}</dd></div>
            {p.submittedOn && <div><dt>Eingereicht am</dt><dd>{formatDay(p.submittedOn)}</dd></div>}
            {p.decidedOn && <div><dt>Entschieden am</dt><dd>{formatDay(p.decidedOn)}{p.decisionNote ? `, ${p.decisionNote}` : ''}</dd></div>}
            {p.contract && <div><dt>Vertrag</dt><dd><Link to={`/finanzierungen/${p.contract.id}`}>{p.contract.number}</Link></dd></div>}
          </dl>
          {p.purpose && <><h3>Verwendungszweck und Begründung</h3><p>{p.purpose}</p></>}
          {p.note && <p className="muted">Bemerkung: {p.note}</p>}
          {mayWrite && (
            <div className="fin-actions">
              <a className="btn btn--primary" href={`/api/v1/financing-projects/${p.id}/report`} target="_blank" rel="noopener">Mappe als PDF</a>
              {!locked && <button type="button" className="btn btn--secondary" onClick={() => setEditing(true)}>Bearbeiten</button>}
              {p.status === 'planned' && <button type="button" className="btn btn--secondary" disabled={move.isPending} onClick={() => move.mutate({ status: 'submitted' })}>Als eingereicht vermerken</button>}
              {p.status === 'submitted' && <button type="button" className="btn btn--secondary" onClick={() => setDecision({ status: 'approved', on: today(), note: '' })}>Bewilligt</button>}
              {p.status === 'submitted' && <button type="button" className="btn btn--secondary" onClick={() => setDecision({ status: 'rejected', on: today(), note: '' })}>Abgelehnt</button>}
              {(p.status === 'submitted' || p.status === 'rejected') && canMoveProject(p.status, 'planned') && <button type="button" className="btn btn--secondary" disabled={move.isPending} onClick={() => move.mutate({ status: 'planned' })}>Zurück auf „Geplant“</button>}
              {p.status === 'approved' && !p.contract && <button type="button" className="btn btn--primary" onClick={() => { setLender(p.lenderName ?? ''); setConverting(true); }}>Vertrag anlegen</button>}
              {!p.contract && <button type="button" className="btn btn--danger" disabled={remove.isPending} onClick={() => { if (window.confirm('Das Vorhaben wirklich löschen?')) remove.mutate(); }}>Löschen</button>}
            </div>
          )}
          {!mayWrite && <p className="fin-actions"><a className="btn btn--primary" href={`/api/v1/financing-projects/${p.id}/report`} target="_blank" rel="noopener">Mappe als PDF</a></p>}
          {locked && <p className="muted small">Ein eingereichtes oder bewilligtes Vorhaben lässt sich nicht mehr ändern, weil die Bank die Zahlen kennt. Setzen Sie es zurück auf „Geplant“, wenn sich etwas ändert.</p>}
        </section>
      )}

      {decision && (
        <form className="card fleet-form" onSubmit={(e) => { e.preventDefault(); move.mutate({ status: decision.status, on: decision.on, note: decision.note.trim() || undefined }); }} aria-label="Entscheidung vermerken">
          <h2>{decision.status === 'approved' ? 'Bewilligung vermerken' : 'Ablehnung vermerken'}</h2>
          <div className="form-grid">
            <label className="field"><span>Datum der Entscheidung</span><input className="input" type="date" value={decision.on} onChange={(e) => setDecision({ ...decision, on: e.target.value })} required /></label>
            <label className="field"><span>Vermerk (zum Beispiel Konditionen oder Grund)</span><input className="input" value={decision.note} onChange={(e) => setDecision({ ...decision, note: e.target.value })} maxLength={1000} /></label>
          </div>
          <div className="form-actions fin-form-actions">
            <button type="submit" className="btn btn--primary" disabled={move.isPending}>Vermerken</button>
            <button type="button" className="btn btn--secondary" onClick={() => setDecision(null)}>Abbrechen</button>
          </div>
        </form>
      )}

      {converting && (
        <form className="card fleet-form" onSubmit={(e) => { e.preventDefault(); setError(null); convert.mutate({ startDate, lenderName: lender.trim() || undefined, contractNumber: contractNumber.trim() || null }); }} aria-label="Vertrag anlegen">
          <h2>Vertrag aus dem Vorhaben anlegen</h2>
          <p className="muted">Wirkia übernimmt die Angaben des Vorhabens und rechnet den Zahlungsplan. Tragen Sie den Beginn laut Vertrag ein.</p>
          <div className="form-grid">
            <label className="field"><span>Vertragsbeginn (erste Rate am)</span><input className="input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required /></label>
            <label className="field"><span>Geldgeber laut Vertrag</span><input className="input" value={lender} onChange={(e) => setLender(e.target.value)} maxLength={150} required /></label>
            <label className="field"><span>Vertragsnummer</span><input className="input" value={contractNumber} onChange={(e) => setContractNumber(e.target.value)} maxLength={100} /></label>
          </div>
          <div className="form-actions fin-form-actions">
            <button type="submit" className="btn btn--primary" disabled={convert.isPending}>{convert.isPending ? 'Wird angelegt …' : 'Vertrag anlegen'}</button>
            <button type="button" className="btn btn--secondary" onClick={() => setConverting(false)}>Abbrechen</button>
          </div>
        </form>
      )}

      <AffordabilityCard assessment={p.assessment} title={snap ? 'Tragfähigkeit heute' : 'Tragfähigkeit'} />
      {snap && (
        <section className="card" aria-label="Stand beim Einreichen">
          <h2>Stand beim Einreichen ({formatDay(p.submittedOn)})</h2>
          <p className="muted small">So sah die Rechnung aus, als Sie das Vorhaben eingereicht haben. Die Mappe zeigt immer den Stand von heute.</p>
          <p className={`fin-rating fin-rating--${snap.affordability.rating}`}>{snap.affordability.summary}</p>
        </section>
      )}
      <p className="muted small">{PROJECT_REPORT_DISCLAIMER} Details zu Löhnen, festen Kosten und Raten stehen unter <Link to="/finanzierungen/kosten">Kostenstruktur</Link>.</p>
    </>
  );
}
